import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { useScrollReveal, useCountUp } from '../hooks/useScrollReveal';

const ValorOS = () => {
  useScrollReveal();
  useCountUp();

  useEffect(() => {
    // Feature showcase glyph switching
    const glyphs = {
      eye: '<circle cx="12" cy="12" r="3"/><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z"/>',
      layers: '<path d="M12 2 2 7l10 5 10-5-10-5Z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>',
      sliders: '<line x1="4" y1="6" x2="20" y2="6"/><circle cx="9" cy="6" r="2"/><line x1="4" y1="12" x2="20" y2="12"/><circle cx="15" cy="12" r="2"/><line x1="4" y1="18" x2="20" y2="18"/><circle cx="7" cy="18" r="2"/>',
      shield: '<path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/>',
      flask: '<path d="M9 2v6L4 20a1 1 0 0 0 1 2h14a1 1 0 0 0 1-2L15 8V2"/><path d="M9 2h6"/><path d="M7 14h10"/>'
    };
    
    const glyphEl = document.getElementById('featureGlyph');
    const items = document.querySelectorAll('.feature-item');
    
    const fio = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          items.forEach(i => i.classList.remove('active'));
          entry.target.classList.add('active');
          const icon = entry.target.getAttribute('data-icon');
          if (glyphEl && glyphs[icon]) {
            glyphEl.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' + glyphs[icon] + '</svg>';
          }
        }
      });
    }, { threshold: 0.5, rootMargin: '-40% 0px -40% 0px' });
    
    items.forEach(i => fio.observe(i));
    if (items[0]) items[0].classList.add('active');

    return () => fio.disconnect();
  }, []);

  return (
    <>
      <Nav darkMode={true} />
      
      <section className="hero-dark">
        <div className="grid-bg"></div>
        <div className="hero-dark-inner">
          <div className="pill reveal in">VALOR OS</div>
          <h1 className="reveal in">Launch, deliver, and govern your programs. All in one OS.</h1>
          <p className="sub reveal in">The secure central hub where your team tracks, manages, and finetunes every case, workflow, and compliance record.</p>
          <div className="hero-ctas reveal in" style={{display:'flex', gap:'14px', justifyContent:'center', marginBottom:'64px'}}>
            <a href="#contact" className="btn btn-accent">Start now <span className="arrow">→</span></a>
          </div>
        </div>
        <div className="os-mock-row reveal">
          <div className="os-mock-card">
            <div className="os-mock-topbar"><span></span><span></span><span></span></div>
            <div className="os-mock-body">
              <div className="row w1"></div><div className="row w2"></div><div className="row w3"></div>
              <div className="row w2"></div><div className="row w1"></div>
            </div>
          </div>
          <div className="os-mock-card center">
            <div className="os-mock-topbar"><span></span><span></span><span></span></div>
            <div className="os-mock-body">
              <span className="os-stat-chip"><span className="dot"></span> 128 active cases</span>
              <div className="row w1" style={{marginTop:'8px'}}></div><div className="row w3"></div><div className="row w2"></div>
            </div>
          </div>
          <div className="os-mock-card">
            <div className="os-mock-topbar"><span></span><span></span><span></span></div>
            <div className="os-mock-body">
              <div className="row w2"></div><div className="row w1"></div><div className="row w3"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="intro-line">
        <div className="wrap">
          <span className="eyebrow" style={{display:'block', marginBottom:'18px'}}>MULTI-PROGRAM CAPABLE</span>
          <p className="reveal">Manage multiple programs in one place. Our interface adapts to each case type, so you can configure it around your agency's mandate &mdash; not the other way around.</p>
        </div>
      </section>

      <section className="fog-section" id="platform">
        <div className="wrap section-head reveal">
          <span className="eyebrow">The Platform</span>
          <h2>Everything your program office needs, in one place.</h2>
        </div>
        <div className="feature-stack">
          <div className="feature-visual">
            <div className="glyph" id="featureGlyph">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z"/>
              </svg>
            </div>
          </div>
          <div className="feature-items">
            <div className="feature-item" data-icon="eye">
              <span className="num">01 &middot; CASE INTELLIGENCE</span>
              <h3>Understand every case.</h3>
              <p>Manage case records, gain full audit visibility, and improve the constituent experience from intake to closure.</p>
            </div>
            <div className="feature-item" data-icon="layers">
              <span className="num">02 &middot; RECORD MANAGEMENT</span>
              <h3>Service cases responsively, in real time.</h3>
              <p>View rich activity data so your team can adapt to changing case needs instantly, not on a weekly report cycle.</p>
            </div>
            <div className="feature-item" data-icon="sliders">
              <span className="num">03 &middot; PROGRAM CONFIGURATION</span>
              <h3>Finetune your programs.</h3>
              <p>From advanced automations to customizable workflows, manage the things that make each program run.</p>
            </div>
            <div className="feature-item" data-icon="shield">
              <span className="num">04 &middot; ACCESS &amp; ADMIN</span>
              <h3>Collaborate compliantly.</h3>
              <p>Ensure maximum transparency and security with manageable access levels for every team member and partner agency.</p>
            </div>
            <div className="feature-item" data-icon="flask">
              <span className="num">05 &middot; VALOR LAB <span className="beta">BETA</span></span>
              <h3>Look into the future.</h3>
              <p>Simulate future scenarios to test policy configurations, or preview the impact of a change before it goes live.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="stat-callout">
        <div className="wrap">
          <p className="lede reveal">Keep constituents served, and your team unburdened. Less manual rework, faster resolutions, fewer dropped handoffs.</p>
          <div className="stats-2 reveal-stagger">
            <div className="stat"><h3 data-count="40" data-suffix="%">0</h3><p>Faster case turnaround</p></div>
            <div className="stat"><h3 data-count="3" data-suffix="x">0</h3><p>Fewer manual handoffs</p></div>
          </div>
        </div>
      </section>

      <section className="fog-section">
        <div className="wrap twin-blurb">
          <div className="col reveal">
            <span className="eyebrow" style={{display:'block', marginBottom:'14px'}}>Compliance</span>
            <h3>Compliance, handled.</h3>
            <p>Every action logged to an immutable record. Satisfy oversight boards and auditors with reporting that's built in, not bolted on.</p>
          </div>
          <div className="col reveal">
            <span className="eyebrow" style={{display:'block', marginBottom:'14px'}}>Support</span>
            <h3>Experts who get government.</h3>
            <p>Public-sector delivery is complex. Our dedicated implementation team makes it simple &mdash; from workflow design to staff onboarding.</p>
            <Link to="/#customers">Meet the team behind Valor <span className="arrow">→</span></Link>
          </div>
        </div>
      </section>

      <section id="customers">
        <div className="wrap">
          <div className="quote-card reveal">
            <p className="q">"Valor OS handles policy enforcement across the entire permit lifecycle &mdash; automatically adjusting review cadence based on staff capacity."</p>
            <p className="who">Program Director &middot; Regional Transit Authority (illustrative)</p>
            <Link to="/#customers" className="readmore">Read more <span className="arrow">→</span></Link>
          </div>
        </div>
      </section>

      <section className="fog-section" id="solutions">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">Products</span>
            <h2>A delivery platform built to scale with your agency.</h2>
          </div>
          <div className="grid-6 reveal-stagger">
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 6h16M4 12h16M4 18h10"/>
                </svg>
              </div>
              <h3>Case Management</h3>
              <p>Unify intake, review, and disposition into a single case record &mdash; no more swivel-chairing between systems.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/>
                </svg>
              </div>
              <h3>Secure Data Exchange</h3>
              <p>Move sensitive constituent data between agencies and partners with end-to-end encryption and full chain-of-custody logging.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8"/>
                </svg>
              </div>
              <h3>Workflow Automation</h3>
              <p>Design no-code approval chains and task routing so staff spend less time on process, more on constituents.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19V5M4 19h16M8 15v-4M13 15V7M18 15v-8"/>
                </svg>
              </div>
              <h3>Insights &amp; Reporting</h3>
              <p>Generate the reports your oversight board actually asks for &mdash; program KPIs, budget burn, and SLA compliance.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-12M6 9l-3 3 3 3M18 9l3 3-3 3"/>
                </svg>
              </div>
              <h3>Sandbox &amp; Preview</h3>
              <p>Trial new policy logic against historical data before it ever touches a live constituent record.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11Z"/>
                </svg>
              </div>
              <h3>Compliance Guard</h3>
              <p>Continuous, always-on checks that flag drift from statutory rules before it becomes a finding.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ValorOS;
